/**
 * Shared loading / error / empty wrapper for admin lists.
 *
 * Standardizes the "I'm a list page powered by useQuery" UX:
 *   - skeleton row while fetching first time
 *   - error banner with Retry button (calls query.refetch())
 *   - empty state when the query returns []
 *   - the actual children when data is present
 *
 * Usage:
 *   const q = useQuery(adminProjectsQuery());
 *   <AdminListShell
 *     query={q}
 *     isEmpty={(d) => d.projects.length === 0}
 *     emptyText="No projects yet."
 *   >
 *     {(data) => <Rows rows={data.projects} />}
 *   </AdminListShell>
 */

import type { ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";

interface AdminListShellProps<TData> {
  query: UseQueryResult<TData, Error>;
  isEmpty?: (data: TData) => boolean;
  emptyText?: string;
  /** Skeleton row count while loading. */
  skeletonRows?: number;
  children: (data: TData) => ReactNode;
}

export function AdminListShell<TData>({
  query,
  isEmpty,
  emptyText = "Nothing here yet.",
  skeletonRows = 3,
  children,
}: AdminListShellProps<TData>) {
  // First load, show skeletons.
  if (query.isPending) {
    return (
      <div className="divide-y divide-border">
        {Array.from({ length: skeletonRows }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-3" aria-hidden="true">
            <div className="h-10 w-16 shrink-0 animate-pulse rounded bg-card" />
            <div className="min-w-0 flex-1 space-y-2">
              <div className="h-3 w-2/5 animate-pulse rounded bg-card" />
              <div className="h-2.5 w-1/4 animate-pulse rounded bg-card" />
            </div>
            <div className="h-6 w-16 animate-pulse rounded-full bg-card" />
          </div>
        ))}
        <span className="sr-only">Loading…</span>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
        <p className="text-sm text-destructive">{query.error.message || "Something went wrong."}</p>
        <button
          type="button"
          onClick={() => query.refetch()}
          disabled={query.isFetching}
          className="rounded-full border border-border px-4 py-1.5 text-xs font-medium text-foreground hover:bg-card disabled:opacity-50"
        >
          {query.isFetching ? "Retrying…" : "Retry"}
        </button>
      </div>
    );
  }

  const data = query.data as TData;
  if (isEmpty?.(data)) {
    return <div className="px-4 py-12 text-center text-sm text-muted-foreground">{emptyText}</div>;
  }

  return <>{children(data)}</>;
}
